import { FC } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Grid, Typography } from "@mui/material";
import {
  Dashboard,
  MonetizationOn,
  LocalOffer,
  Business,
  Storefront,
  PointOfSale,
  Style,
  ManageAccounts,
  AdminPanelSettings,
  Lock,
  Computer,
  AccessTime,
  AssignmentLate,
  Replay,
  PriceChange,
  FmdGood,
  Warning,
  Inventory2,
  ViewModule,
  AttachMoney,
  VpnKey,
  Description,
} from "@mui/icons-material";

const mainItems = [
  {
    title: "Dashboard",
    link: "/admin/dashboard",
    icon: <Dashboard />,
  },
];

const configItems = [
  {
    title: "Price List",
    link: "/admin/config/upload-price",
    icon: <MonetizationOn />,
  },
  {
    title: "Discounts",
    link: "/admin/discounts",
    icon: <LocalOffer />,
  },
  {
    title: "Merchants",
    link: "/admin/config/merchants",
    icon: <Business />,
  },
  {
    title: "Stores",
    link: "/admin/config/merchants/presto/stores?merchantId=1",
    icon: <Storefront />,
  },
  {
    title: "Device Access",
    link: "/admin/config/merchants/presto/stores/1/points",
    icon: <PointOfSale />,
  },
  {
    title: "PinTag Config",
    link: "/admin/config/merchants/presto/stores/1/pin-tag",
    icon: <Style />,
  },
  {
    title: "Racks",
    link: "/admin/racks",
    icon: <ViewModule />,
  },
  {
    title: "Box Config",
    link: "/admin/boxConfig",
    icon: <Inventory2 />,
  },
  {
    title: "User Management",
    link: "/admin/user-management",
    icon: <ManageAccounts />,
  },
  {
    title: "Role Management",
    link: "/admin/role-management",
    icon: <AdminPanelSettings />,
  },
  {
    title: "Permission Management",
    link: "/admin/permission-management",
    icon: <Lock />,
  },
  {
    title: "Station Management",
    link: "/admin/station-management",
    icon: <Computer />,
  },
  {
    title: "Opened Stations",
    link: "/admin/opened-stations",
    icon: <AccessTime />,
  },
  {
    title: "Manager Access Logs",
    link: "/admin/manager-access-logs",
    icon: <VpnKey />,
  },
];

const otherItems = [
  {
    title: "Releases",
    link: "/admin/releases",
    icon: <Description />,
  },
  {
    title: "Instructions",
    link: "/admin/instructions",
    icon: <AssignmentLate />,
  },
  {
    title: "Redo Reasons",
    link: "/admin/redo-reasons",
    icon: <Replay />,
  },
  {
    title: "Cash Drawer Reasons",
    link: "/admin/cash-drawer-reasons",
    icon: <AttachMoney />,
  },
  {
    title: "Upcharges",
    link: "/admin/upcharges",
    icon: <PriceChange />,
  },
  {
    title: "Spots",
    link: "/admin/spots",
    icon: <FmdGood />,
  },
  {
    title: "Price Setup",
    link: "/admin/price-setup",
    icon: <PriceChange />,
  },
  {
    title: "Price Overrides",
    link: "/admin/price-overrides",
    icon: <PriceChange />,
  },
  {
    title: "Damages",
    link: "/admin/damages",
    icon: <Warning />,
  },
];

const MenuItem: FC<{
  title: string;
  link: string;
  icon: any;
  active: boolean;
}> = (props) => {
  const navigate = useNavigate();
  const { title, link, icon, active } = props;

  return (
    <Grid
      sx={{
        display: "flex",
        alignItems: "center",
        cursor: "pointer",
        backgroundColor: active ? "#ffffff" : "transparent",
        borderRadius: "0.21rem",
        mx: "0.5rem",
        p: "0.6rem",
        pl: "0.85rem",
        mb: "0.1rem",
        boxShadow: active ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
        "&:hover": {
          backgroundColor: active ? "#ffffff" : "rgba(0,0,0,0.04)",
        },
      }}
      onClick={() => navigate(link)}
    >
      <Box
        sx={{
          color: active ? "#018752" : "#515151",
          display: "flex",
          alignItems: "center",
          "& .MuiSvgIcon-root": { fontSize: "1.6rem" },
        }}
      >
        {icon}
      </Box>
      <Box sx={{ pl: "0.85rem" }}>
        <Typography
          sx={{
            color: active ? "#018752" : "#515151",
            fontSize: "1.3rem",
            fontWeight: active ? 600 : 400,
          }}
        >
          {title}
        </Typography>
      </Box>
    </Grid>
  );
};

const SectionLabel: FC<{ label: string }> = ({ label }) => (
  <Box sx={{ pl: "1.35rem", pb: "0.3rem", mt: "1.5rem", mb: "0.2rem" }}>
    <Typography
      sx={{
        fontSize: "1.2rem",
        fontWeight: 400,
        color: "#515151",
      }}
    >
      {label}
    </Typography>
  </Box>
);

export const AdminSideNav = () => {
  const location = useLocation();
  const thisYear = new Date().getFullYear();

  return (
    <Grid
      sx={{
        height: "100%",
        width: "21.64rem",
        backgroundColor: "#ffffff",
        boxShadow: "0.142857143rem 0rem 0.71rem -0.285714286rem rgba(0,0,0,0.18)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Grid flex={1} sx={{ overflowY: "auto", overflowX: "hidden" }}>
        {/* Company logo & name */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            pt: "1.5rem",
            pb: "1rem",
          }}
        >
          <Box
            sx={{
              width: "4.93rem",
              height: "4.93rem",
              borderRadius: "50%",
              backgroundColor: "#018752",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography sx={{ color: "#ffffff", fontSize: "2rem", fontWeight: 700 }}>
              P
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: "1.43rem",
              fontWeight: 600,
              color: "#515151",
              textAlign: "center",
              mt: "0.71rem",
              px: "1rem",
            }}
          >
            Presto Valet
          </Typography>
        </Box>

        {/* Main section */}
        <SectionLabel label="Main" />
        {mainItems.map((item) => (
          <MenuItem
            key={item.link}
            link={item.link}
            title={item.title}
            icon={item.icon}
            active={location.pathname === item.link}
          />
        ))}

        {/* Configs section */}
        <SectionLabel label="Configs" />
        {configItems.map((item) => (
          <MenuItem
            key={item.link}
            link={item.link}
            title={item.title}
            icon={item.icon}
            active={location.pathname.startsWith(item.link.split("?")[0])}
          />
        ))}

        {/* Others section */}
        <SectionLabel label="Others" />
        {otherItems.map((item) => (
          <MenuItem
            key={item.link}
            link={item.link}
            title={item.title}
            icon={item.icon}
            active={location.pathname === item.link}
          />
        ))}
      </Grid>

      {/* Footer */}
      <Grid sx={{ pb: "0.71rem" }}>
        <Typography
          sx={{
            fontSize: "0.71rem",
            color: "#b3b3b3",
            textAlign: "center",
            lineHeight: 1.6,
          }}
        >
          All rights reserved.
          <br />
          Presto Valet &copy; {thisYear}
          <br />
          <Box
            component="span"
            sx={{ textDecoration: "underline", cursor: "pointer" }}
          >
            Privacy Policy
          </Box>{" "}
          and{" "}
          <Box
            component="span"
            sx={{ textDecoration: "underline", cursor: "pointer" }}
          >
            Terms and Conditions
          </Box>
        </Typography>
      </Grid>
    </Grid>
  );
};
